// Pure helpers for AI analysis findings. No Vue, no store, no '@/' imports:
// these run under `node --test` as well as in the browser bundle.

// The API stores UTC but serialises entities read back from SQL without a timezone designator
// ("2026-09-07T08:01:36.6270633"), which browsers parse as local time. Anything already carrying
// a Z or an offset, or that is not an ISO date-time string, passes through untouched.
const ISO_WITHOUT_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
export function toUtcIso(value) {
  return typeof value === 'string' && ISO_WITHOUT_OFFSET.test(value) ? `${value}Z` : value;
}

const FINDING_DATE_FIELDS = Object.freeze(['createDate', 'updateDate', 'approvalStatusChangedOn']);

/** New finding object with its API timestamps pinned to UTC. */
export function normalizeFinding(finding) {
  if (!finding) return finding;
  return { ...finding, ...Object.fromEntries(FINDING_DATE_FIELDS.filter((k) => finding[k] != null).map((k) => [k, toUtcIso(finding[k])])) };
}

export const API_JOB_STATUS = Object.freeze({
  PENDING: 'Pending',
  STARTED: 'Started',
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
});

export const JOB_UI_STATUS = Object.freeze({
  RUNNING: 'Running',
  DONE: 'Done',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
});

export const APPROVAL = Object.freeze({ ACCEPTED: 'Accepted', REJECTED: 'Rejected' });
export const SCAB = Object.freeze({ NO: 'NO', POSSIBLE: 'POSSIBLE', YES: 'YES' });

export const REVIEW_STATUS = Object.freeze({
  PENDING: 'Pending',
  IN_REVIEW: 'In review',
  APPROVED: 'Approved',
});

const TERMINAL = new Set([API_JOB_STATUS.COMPLETED, API_JOB_STATUS.FAILED, API_JOB_STATUS.CANCELLED]);

export function toUiStatus(apiStatus) {
  switch (apiStatus) {
    case API_JOB_STATUS.COMPLETED: return JOB_UI_STATUS.DONE;
    case API_JOB_STATUS.FAILED: return JOB_UI_STATUS.FAILED;
    case API_JOB_STATUS.CANCELLED: return JOB_UI_STATUS.CANCELLED;
    default: return JOB_UI_STATUS.RUNNING;
  }
}

export function isTerminalStatus(apiStatus) {
  return TERMINAL.has(apiStatus);
}

const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;

export const STALE_RUNNING_MS = MINUTES_PER_HOUR * SECONDS_PER_MINUTE * MS_PER_SECOND;
export const STALE_RUNNING_MESSAGE = 'Still running after 60 min — check with an admin';

// The live pipeline never sets Failed / Cancelled (API ask 2), so a crashed job looks Running forever.
// Callers re-render on every poll (the jobs array is replaced), which is when `now` advances.
export function isStaleRunning(job, now = Date.now()) {
  if (!job) return false;
  const uiStatus = job.uiStatus || toUiStatus(job.status);
  const started = new Date(job.createDate).getTime();
  return uiStatus === JOB_UI_STATUS.RUNNING && Number.isFinite(started) && now - started > STALE_RUNNING_MS;
}

function toPoint(entry) {
  if (typeof entry === 'string') {
    const [x, y] = entry.split(',').map((v) => Number(v.trim()));
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  }
  if (entry && typeof entry === 'object') {
    const x = Number(entry.x);
    const y = Number(entry.y);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  }
  return null;
}

/** Accepts the API's `[{x,y}]` JSON, the legacy `["x,y"]` JSON, or an array. Returns [] on any problem. */
export function parseMaskPoints(maskPoints) {
  let raw = maskPoints;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (err) {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  const points = raw.map(toPoint);
  return points.every(Boolean) ? points : [];
}

const MIN_POLYGON_POINTS = 3;

export function polygonArea(points) {
  if (!Array.isArray(points) || points.length < MIN_POLYGON_POINTS) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

export function relativeArea(points, width, height) {
  const total = Number(width) * Number(height);
  if (!total) return 0;
  return polygonArea(points) / total;
}

function center(finding) {
  return {
    x: Number(finding.imageXCenterCoordinate) || 0,
    y: Number(finding.imageYCenterCoordinate) || 0,
  };
}

/** Top-to-bottom, then left-to-right. Returns new objects carrying `badgeIndex` (1-based). */
export function badgeOrder(findings) {
  return [...(findings || [])]
    .sort((a, b) => {
      const ca = center(a);
      const cb = center(b);
      return ca.y - cb.y || ca.x - cb.x;
    })
    .map((finding, i) => ({ ...finding, badgeIndex: i + 1 }));
}

export function stripRi(value) {
  return value ? String(value).replace(/^ri/i, '').trim() : '';
}

export function stripCat(value) {
  return value ? String(value).replace(/^cat\s*/i, '').trim() : '';
}

export function isScabCandidate(finding) {
  const measure = String(finding?.scabMeasure || '').toUpperCase();
  return measure === SCAB.POSSIBLE || measure === SCAB.YES;
}

const ROUNDING_SCALE = 10000;

function round4(n) {
  return Math.round(n * ROUNDING_SCALE) / ROUNDING_SCALE;
}

const EMPTY_KPIS = Object.freeze({
  total: 0,
  corrosion: 0,
  coatingBreakdown: 0,
  scabCandidates: 0,
  averageConfidence: 0,
  areaMetric: 0,
});

/** `sizeById` = { [imageId]: { width, height } }. Missing sizes contribute 0 area. */
export function computeKpis(findings, sizeById) {
  const list = findings || [];
  if (!list.length) return { ...EMPTY_KPIS };
  const typeOf = (f) => String(f.type || '').toLowerCase();
  const confidence = list.reduce((s, f) => s + (Number(f.detectionConfidence) || 0), 0) / list.length;
  const area = list.reduce((s, f) => {
    const size = (sizeById && sizeById[f.imageId]) || {};
    return s + relativeArea(parseMaskPoints(f.maskPoints), size.width, size.height);
  }, 0);
  return {
    total: list.length,
    corrosion: list.filter((f) => typeOf(f).includes('corrosion')).length,
    coatingBreakdown: list.filter((f) => typeOf(f).includes('coating')).length,
    scabCandidates: list.filter(isScabCandidate).length,
    averageConfidence: round4(confidence),
    areaMetric: round4(area),
  };
}

export function reviewStatus(findings) {
  const list = findings || [];
  if (!list.length) return REVIEW_STATUS.PENDING;
  const decided = list.filter((f) => f.approvalStatus === APPROVAL.ACCEPTED || f.approvalStatus === APPROVAL.REJECTED).length;
  if (decided === 0) return REVIEW_STATUS.PENDING;
  return decided === list.length ? REVIEW_STATUS.APPROVED : REVIEW_STATUS.IN_REVIEW;
}

// Same palette AIInspect.vue uses for Level 1..4, keyed here by the class index the API writes today.
export const SEVERITY_COLORS = Object.freeze({
  0: '#215F9B',
  1: '#D2A000',
  2: '#C04F15',
  3: '#A80000',
});
const SEVERITY_FALLBACK = '#03624c';

export function severityColor(severity) {
  return SEVERITY_COLORS[String(severity)] || SEVERITY_FALLBACK;
}

const HEX_COLOR = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX_RADIX = 16;

/** `#rrggbb` → `rgba(r, g, b, alpha)`; anything that is not a 6-digit hex colour falls back to the severity fallback. */
export function withAlpha(hex, alpha) {
  const match = HEX_COLOR.exec(String(hex || '')) || HEX_COLOR.exec(SEVERITY_FALLBACK);
  const [r, g, b] = match.slice(1).map((channel) => parseInt(channel, HEX_RADIX));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
